import { Link } from 'react-router-dom';
import { StarIcon } from 'lucide-react';
import { REVIEWS_CLAUSE } from '../../data/legal';
import { founder, salon } from '../../data/content';
import { rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { shopLabels } from '../../data/shopLabels';
import type { Product, Review } from '../../core/types/catalog';
import type { Creator } from '../../core/types/site';
type Props = {
  product: Product;
  reviews: Review[];
  /** Een vaste cliënt die dit product als haar favoriet heeft (Instellingen › Cadeaus & codes). Geen review: zonder sterren. */
  client?: Pick<Creator, 'name' | 'quote' | 'image'>;
};

/** Ervaringen: het gemiddelde links (met eventueel de favoriet van een vaste cliënt), geschreven reviews rechts. */
export function ProductReviews({
  product,
  reviews,
  client
}: Props) {
  return <section id="reviews" className="scroll-mt-28 border-t border-line bg-sand py-16 lg:py-20" aria-labelledby="reviews-title" data-id="element-8383" data-mp="1290rhi3bfxc6">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8384" data-mp="169jsnynq11ge">
        <div className="lg:col-span-4" data-id="element-8385" data-mp="1b9ob3u86w699">
          <h2 id="reviews-title" className={product.reviews > 0 ? 'sr-only' : 'type-sub text-ink'} data-id="element-8386" data-mp="1b8mnkq9zlran">
            Ervaringen
          </h2>
          {product.reviews > 0 && <>
              <p className="type-figure text-ink" aria-hidden="true" data-id="element-8387" data-mp="1275c5l7iydsa">
                {rating(product.rating)}
              </p>
              <p className="mt-2 text-[15px] text-muted" data-id="element-8388" data-mp="nhha2hrirb3i">
                <span className="sr-only" data-id="element-8389" data-mp="sbbwvu2f866t">{rating(product.rating)} van 5, </span>
                gemiddeld uit {plural(product.reviews, 'beoordeling', 'beoordelingen')}
              </p>
            </>}
          {client && <figure className="mt-8 border-t border-ink/15 pt-5" data-id="element-8390" data-mp="iiedzmtiof7o">
              <figcaption className="flex items-center gap-3 text-[14px] text-ink" data-id="element-8391" data-mp="4m0vcad8y7rm">
                {client.image && <img src={client.image} alt="" loading="lazy" width={32} height={32} className="h-8 w-8 shrink-0 rounded-full object-cover" data-id="element-8392" data-mp="9ihkn7w6ms6a" />}
                Favoriet van {client.name}, vaste cliënt
              </figcaption>
              <blockquote className="mt-2 text-[15px] leading-relaxed text-muted" data-id="element-8393" data-mp="26g5c52l7bfhv">“{client.quote}”</blockquote>
            </figure>}
        </div>
        <ul className="grid gap-8 sm:grid-cols-2 lg:col-span-8" data-id="element-8394" data-mp="1xicys3z1ihx4">
          {reviews.map(r => <li key={`${r.name}-${r.body.slice(0, 24)}`} className="border-t border-ink/15 pt-5" data-id="element-8395" data-mp="2u6syutzs09oh">
              {/* Altijd vijf sterren, de lege gedempt: zo lees je "4 van 5" in één oogopslag. */}
              <p className="flex gap-0.5" data-id="element-8396" data-mp="2l6cuzjf4f0j3">
                <span className="sr-only" data-id="element-8397" data-mp="2l98uhc6m9x9n">{r.rating} van 5</span>
                {Array.from({
              length: 5
            }).map((_, i) => <StarIcon key={i} className={`h-3.5 w-3.5 fill-current ${i < r.rating ? 'text-clay' : 'text-ink/15'}`} strokeWidth={0} aria-hidden="true" data-id="element-8398" data-mp="19rpml6i3d64r" data-mpi={i} />)}
              </p>
              <blockquote className="mt-3 type-name text-ink" data-id="element-8399" data-mp="uozdi82g6yhz">“{r.body}”</blockquote>
              <p className="mt-3 text-[14px] text-muted" data-id="element-8400" data-mp="un4ooio22m8m">
                {r.name} · {r.context}
                {r.verified && ' · geverifieerd'}
              </p>
            </li>)}
          {reviews.length === 0 && <li className="text-[15px] leading-relaxed text-muted sm:col-span-2" data-id="element-8401" data-mp="19ednniduljyf">
              Nog geen geschreven review voor dit product. Gebruik je het al?{' '}
              <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="text-ink underline underline-offset-4" data-id="element-8402" data-mp="1dhtge3msr05u">
                Laat {founder.firstName} weten wat je ervan vindt
                <span className="sr-only" data-id="element-8403" data-mp="1nbk1cyjpssgs">{shopLabels.viaWhatsApp}</span>
              </a>
              .
            </li>}
        </ul>
        {/* Omnibus: zeggen hoe reviews gecontroleerd worden, met de clausule erbij. */}
        {reviews.length > 0 && <p className="text-[13px] leading-relaxed text-muted lg:col-span-8 lg:col-start-5" data-id="element-8404" data-mp="3u4vx5y08k4sw">
            Alleen wie het product kocht of de behandeling had, kan een review plaatsen. {founder.firstName} plaatst positieve én kritische reviews, ongewijzigd.{' '}
            <Link to={REVIEWS_CLAUSE} className="text-ink underline underline-offset-4" data-id="element-8405" data-mp="1xg8jnhyz8gb7">
              Zo controleer ik reviews
            </Link>
          </p>}
      </div>
    </section>;
}